import type {
  CarnivalViewerState,
  SinChoice,
  SinType,
} from "@cloudnest/carnival-of-sins-engine";
import { useSyncExternalStore } from "react";
import { isPlatformGamePath } from "../../shared/routing/game-path-mode";
import {
  createGameApi,
  type GameApi,
  type GameApiMode,
} from "../../shared/services/game-api";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

function resolveMode(): GameApiMode {
  if (typeof window === "undefined") return "legacy";
  return isPlatformGamePath("carnival-of-sins", window.location.pathname)
    ? "game-platform"
    : "legacy";
}

function getGameApi(): GameApi {
  const nextMode = resolveMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("carnival-of-sins", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

export type ActionResult = {
  readonly success: boolean;
  readonly error?: string;
};

export interface CarnivalOfSinsMultiplayerState {
  readonly viewerState: CarnivalViewerState | null;
  readonly lastError: { readonly error: string } | null;
  readonly connection:
    | "idle"
    | "connecting"
    | "open"
    | "reconnecting"
    | "closed";
  readonly lastAppliedSeq: number;
  readonly roomId: string | null;
  /**
   * The engine playerId this client controls — the real user id, not a fixed
   * p1/p2 slot (this game seats 2-5 players, each identified by their own
   * user id; see apps/board-game-rules-carnival-of-sins-v2/src/transition.ts).
   * null for an observer.
   */
  readonly mySlot: string | null;
  readonly isHost: boolean;
}

interface ViewerStateRow {
  readonly sequenceNumber: number;
  readonly state: Record<string, unknown>;
}

const INITIAL_STATE: CarnivalOfSinsMultiplayerState = {
  viewerState: null,
  lastError: null,
  connection: "idle",
  lastAppliedSeq: 0,
  roomId: null,
  mySlot: null,
  isHost: false,
};

let state: CarnivalOfSinsMultiplayerState = { ...INITIAL_STATE };
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** Keep the highest-sequence projected row — the server already redacted it. */
function adoptLatest(rows: readonly ViewerStateRow[], fromSeq: number): number {
  let bestSeq = fromSeq;
  let best: Record<string, unknown> | null = null;
  for (const row of rows) {
    if (row.sequenceNumber > bestSeq) {
      bestSeq = row.sequenceNumber;
      best = row.state;
    }
  }
  if (best !== null) {
    state = {
      ...state,
      viewerState: best as unknown as CarnivalViewerState,
      lastAppliedSeq: bestSeq,
    };
  }
  return bestSeq;
}

function setError(error: unknown, fallback: string): void {
  state = {
    ...state,
    lastError: { error: error instanceof Error ? error.message : fallback },
  };
  notify();
}

async function fetchAndAdopt(roomId: string, fromSeq: number): Promise<void> {
  const res = await getGameApi().getViewerStates(roomId, fromSeq);
  if (res.success && res.data && state.roomId === roomId) {
    adoptLatest(res.data, fromSeq);
  }
}

/** Submit one command, then pull the caller's freshly projected viewer state. */
async function submitThenSync(
  roomId: string,
  eventType: string,
  payload: Record<string, unknown>,
): Promise<ActionResult> {
  try {
    const res = await getGameApi().submitEvent(roomId, eventType, payload);
    if (!res.success) {
      const error = res.error ?? "ERR_CARNIVAL_OF_SINS_ACTION";
      state = { ...state, lastError: { error } };
      notify();
      return { success: false, error };
    }
    await fetchAndAdopt(roomId, state.lastAppliedSeq);
    if (state.roomId !== roomId) return { success: true };
    state = { ...state, lastError: null };
    notify();
    return { success: true };
  } catch (error) {
    setError(error, "ERR_CARNIVAL_OF_SINS_ACTION");
    return { success: false, error: "ERR_CARNIVAL_OF_SINS_ACTION" };
  }
}

function withRoom(
  fn: (roomId: string) => Promise<ActionResult>,
): Promise<ActionResult> {
  const roomId = state.roomId;
  if (!roomId) return Promise.resolve({ success: false, error: "ERR_NO_GAME" });
  return fn(roomId);
}

async function beginSession(
  roomId: string,
  mySlot: string | null,
  isHost: boolean,
): Promise<void> {
  state = {
    ...INITIAL_STATE,
    roomId,
    mySlot,
    isHost,
    connection: "connecting",
  };
  notify();
}

export const carnivalOfSinsMultiplayerActions = {
  initializeAsHost: async (roomId: string, myUserId: string): Promise<void> => {
    await beginSession(roomId, myUserId, true);
    try {
      const existing = await getGameApi().getViewerStates(roomId);
      const rows = existing.success && existing.data ? existing.data : [];
      if (rows.length > 0) {
        adoptLatest(rows, 0);
      } else {
        // Server builds the authoritative initial state from room config
        // (one engine playerId per participantUserId); the Sync then
        // materialises the first per-viewer projections.
        await getGameApi().submitEvent(roomId, "init", {});
        await getGameApi().submitEvent(roomId, "SyncViewerState", {});
        await fetchAndAdopt(roomId, 0);
      }
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_CARNIVAL_OF_SINS_INIT_FAILED");
    }
  },

  initializeAsGuest: async (
    roomId: string,
    myUserId: string,
  ): Promise<void> => {
    await beginSession(roomId, myUserId, false);
    try {
      await fetchAndAdopt(roomId, 0);
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_CARNIVAL_OF_SINS_FETCH_FAILED");
    }
  },

  initializeAsObserver: async (roomId: string): Promise<void> => {
    await beginSession(roomId, null, false);
    try {
      await fetchAndAdopt(roomId, 0);
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_CARNIVAL_OF_SINS_FETCH_FAILED");
    }
  },

  replayEvents: async (roomId: string): Promise<void> => {
    try {
      await fetchAndAdopt(roomId, state.lastAppliedSeq);
      notify();
    } catch (error) {
      setError(error, "ERR_CARNIVAL_OF_SINS_REPLAY_FAILED");
    }
  },

  applyRemoteEvent: (
    payloadState: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;
    state = {
      ...state,
      viewerState: payloadState as unknown as CarnivalViewerState,
      lastAppliedSeq: seq,
    };
    notify();
  },

  selectSin: (sin: SinType): Promise<ActionResult> =>
    withRoom((roomId) => submitThenSync(roomId, "SelectSin", { sin })),

  revealSin: (choice: SinChoice): Promise<ActionResult> =>
    withRoom((roomId) => submitThenSync(roomId, "RevealSin", { choice })),

  clearError: (): void => {
    state = { ...state, lastError: null };
    notify();
  },

  reset: (): void => {
    state = { ...INITIAL_STATE };
    notify();
  },
};

export function useCarnivalOfSinsMultiplayerStore(): CarnivalOfSinsMultiplayerState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}
